import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { api, ApiError } from '../api/client'
import type { InvoiceEvent } from '../api/client'

export function InvoiceDetailPage() {
  const { invoiceId = '' } = useParams()
  const qc = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => api.invoices.detail(invoiceId),
    enabled: Boolean(invoiceId),
  })

  const reprocess = useMutation({
    mutationFn: () => api.invoices.reprocess(invoiceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', invoiceId] })
    },
  })

  const reset = useMutation({
    mutationFn: () => api.invoices.reset(invoiceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', invoiceId] })
    },
  })

  if (isLoading) return <div>Loading invoice…</div>
  if (error) return <div className="text-red-600">Failed to load: {(error as Error).message}</div>
  if (!data) return null

  return (
    <div className="max-w-4xl">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-slate-500">Invoice</div>
          <h1 className="font-mono text-xl font-bold">
            {data.invoiceNumber ?? data.id.slice(0, 8)}
            {data.stornoOfInvoiceId && (
              <Link
                to={`/invoices/${data.stornoOfInvoiceId}`}
                title={`Storno of ${data.stornoOfInvoiceId}`}
                className="ml-3 inline-flex items-center rounded bg-red-100 px-2 py-0.5 align-middle text-xs font-bold text-red-800 hover:bg-red-200"
              >
                <span aria-hidden="true" className="mr-1">✕</span>STORNO
              </Link>
            )}
          </h1>
          <div className="text-xs text-slate-500">{data.id}</div>
        </div>
        <Link to="/invoices" className="text-sm text-brand-700 hover:underline">← back</Link>
      </div>

      <section className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Status" value={data.status} mono />
        <Field label="NAV" value={data.taxReportingStatus} mono />
        <Field label="Attempts" value={String(data.attemptCount)} />
        <Field label="Merchant" value={data.merchantDisplayName ?? data.merchantId} />
        <Field label="Integrator" value={data.integratorId} mono />
        <Field label="Country" value={data.countryCode} />
        <Field label="NAV tx" value={data.taxSubmissionId ?? '—'} mono />
        <Field label="Submitted at" value={data.taxSubmittedAt ? new Date(data.taxSubmittedAt).toLocaleString() : '—'} />
        <Field label="Confirmed at" value={data.taxConfirmedAt ? new Date(data.taxConfirmedAt).toLocaleString() : '—'} />
      </section>

      <section className="mb-4">
        <h2 className="mb-2 text-sm font-semibold uppercase text-slate-600">Storage</h2>
        <div className="space-y-1 rounded border border-slate-200 bg-white p-3 text-xs">
          <StorageRow
            label="PDF"
            internalName={data.pdfS3Key ?? data.legacyStoredPdfUrl}
            downloadUrl={data.downloadUrls?.pdf ?? null}
          />
          <StorageRow
            label="NAV request XML (as sent)"
            internalName={data.navRequestXmlKey}
            downloadUrl={data.downloadUrls?.navRequestXml ?? null}
          />
          <StorageRow
            label="NAV request XML (internal, full customer data)"
            internalName={data.navInternalRequestXmlKey}
            downloadUrl={data.downloadUrls?.navInternalRequestXml ?? null}
          />
          <StorageRow
            label="NAV response XML"
            internalName={data.navResponseXmlKey}
            downloadUrl={data.downloadUrls?.navResponseXml ?? null}
          />
          <StorageRow
            label="NAV poll response XML"
            internalName={data.navPollResponseXmlKey}
            downloadUrl={data.downloadUrls?.navPollResponseXml ?? null}
          />
          <StorageRow
            label={
              <span>
                UBL 2.1 (EN 16931)
                {data.ublValidationStatus === 'PASSED' && (
                  <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                    PASSED
                  </span>
                )}
                {data.ublValidationStatus === 'FAILED' && (
                  <span className="ml-2 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700">
                    FAILED
                  </span>
                )}
                {data.ublValidationStatus === null && data.ublS3Key && (
                  <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
                    VALIDATING
                  </span>
                )}
              </span>
            }
            internalName={data.ublS3Key}
            downloadUrl={data.downloadUrls?.ublXml ?? null}
          />
          <StorageRow
            label="UBL validation report (CEN EN 16931 SVRL)"
            internalName={data.ublValidationSvrlS3Key}
            downloadUrl={data.downloadUrls?.ublValidationSvrlXml ?? null}
          />
          <StorageRow
            label="UBL validation report (Peppol BIS 3.0 SVRL)"
            internalName={data.ublValidationPeppolSvrlS3Key}
            downloadUrl={data.downloadUrls?.ublValidationPeppolSvrlXml ?? null}
          />
          {data.ublValidationAt && (
            <div className="mt-1 pl-2 text-[11px] text-slate-500">
              Validated at: {new Date(data.ublValidationAt).toLocaleString()}
            </div>
          )}
        </div>
      </section>

      <section className="mb-4">
        <h2 className="mb-2 text-sm font-semibold uppercase text-slate-600">Inbound payload (DTO)</h2>
        <PayloadBlock payload={data.invoiceData} />
      </section>

      <section className="mb-4">
        <h2 className="mb-2 text-sm font-semibold uppercase text-slate-600">Latest inbound receipt</h2>
        {data.latestReceipt ? (
          <div className="rounded border border-slate-200 bg-white p-3 text-xs">
            <div><span className="text-slate-500">Received at: </span>{new Date(data.latestReceipt.receivedAt).toLocaleString()}</div>
            <div><span className="text-slate-500">Signature valid: </span>{data.latestReceipt.sigValid ? 'yes' : 'no'}</div>
            <div><span className="text-slate-500">Payload hash: </span><code className="break-all">{data.latestReceipt.payloadHash}</code></div>
            <div><span className="text-slate-500">Chain hash: </span><code className="break-all">{data.latestReceipt.chainHash}</code></div>
            <div><span className="text-slate-500">Request id: </span><code>{data.latestReceipt.requestId}</code></div>
          </div>
        ) : (
          <div className="text-xs text-slate-500">No receipt on file (idempotency_key missing).</div>
        )}
      </section>

      <section className="mb-4">
        <h2 className="mb-2 text-sm font-semibold uppercase text-slate-600">Digitális aláírás</h2>
        {data.signature == null ? (
          <div className="text-xs text-slate-400">Nincs aláírás</div>
        ) : (
          <div className="rounded border border-slate-200 bg-white p-3 text-xs space-y-1">
            <div>
              <span className="text-slate-500">Szint: </span>
              <code className="font-mono">{data.signature.level}</code>
            </div>
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-slate-500">Aláíró tanúsítvány SHA-256: </span>
              <code className="font-mono">{data.signature.signerCertSha256.slice(0, 12)}…</code>
              <button
                type="button"
                onClick={() => void navigator.clipboard.writeText(data.signature!.signerCertSha256)}
                className="text-brand-700 hover:underline"
              >
                copy
              </button>
            </div>
            <div>
              <span className="text-slate-500">Aláírás ideje: </span>
              {new Date(data.signature.signedAt).toLocaleString()}
            </div>
            <div>
              <span className="text-slate-500">TSA URL: </span>
              <a href={data.signature.tsaUrl} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline font-mono break-all">
                {data.signature.tsaUrl}
              </a>
            </div>
            <div>
              <span className="text-slate-500">Következő időbélyeg frissítés: </span>
              {relativeTime(data.signature.refreshDueAt)}
            </div>
          </div>
        )}
      </section>

      <section className="mb-4">
        <h2 className="mb-2 text-sm font-semibold uppercase text-slate-600">Event timeline</h2>
        <ol className="space-y-2">
          {data.events.map((e) => (
            <TimelineRow key={e.id} event={e} />
          ))}
        </ol>
      </section>

      <section className="mt-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={reprocess.isPending}
          onClick={() => {
            if (confirm('Requeue every failed pdf/nav_submission job for this invoice?')) {
              reprocess.mutate()
            }
          }}
          className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-40"
        >
          {reprocess.isPending ? 'Requeuing…' : 'Reprocess failed jobs'}
        </button>
        {reprocess.data && (
          <span className="text-sm text-emerald-700">
            Requeued: {reprocess.data.pdfJobsRequeued} PDF · {reprocess.data.navSubmissionJobsRequeued} NAV
          </span>
        )}
        {reprocess.error && (
          <span className="text-sm text-red-600">
            {reprocess.error instanceof ApiError
              ? `${reprocess.error.status}: ${reprocess.error.message}`
              : (reprocess.error as Error).message}
          </span>
        )}

        <button
          type="button"
          disabled={reset.isPending}
          onClick={() => {
            if (confirm(
              'Force-reset EVERY job for this invoice back to PENDING and flip tax_reporting_status ' +
              'from FAILED/REJECTED back to PENDING? ACCEPTED / completed rows are preserved. ' +
              'Use this after fixing the underlying cause of a persistent failure.',
            )) {
              reset.mutate()
            }
          }}
          className="rounded bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40"
        >
          {reset.isPending ? 'Resetting…' : 'Force reset for retry'}
        </button>
        {reset.data && (
          <span className="text-sm text-emerald-700">
            Reset: {reset.data.navJobsReset} NAV · {reset.data.pdfJobsReset} PDF
            {reset.data.taxReportingStatusReset ? ' · tax_reporting_status → PENDING' : ''}
          </span>
        )}
        {reset.error && (
          <span className="text-sm text-red-600">
            {reset.error instanceof ApiError
              ? `${reset.error.status}: ${reset.error.message}`
              : (reset.error as Error).message}
          </span>
        )}
      </section>
    </div>
  )
}

// ── Timeline ──────────────────────────────────────────────────────────

/**
 * One event = one card. Renders a colour-coded severity dot, the
 * event name, a short human summary of the meta payload, an optional
 * state transition tag when the event actually moved status, the
 * actor that fired it, and an expandable raw-JSON meta block.
 *
 * The old table dropped everything in `meta` on the floor, so the UI
 * showed `NAV_FAILED persisted → persisted worker` with no hint at
 * WHY the job failed. Every worker path already writes rich meta
 * (attempts, navTransactionId, navErrorCode, lastError, pdfS3Key,
 * reason). Surface it.
 */
function TimelineRow({ event: e }: { event: InvoiceEvent }) {
  const [open, setOpen] = useState(false)
  const severity = severityFor(e.eventName)
  const meta = (e.meta ?? {}) as Record<string, unknown>
  const summary = summariseMeta(meta)
  const isTransition = e.fromState !== e.toState && e.fromState !== null

  return (
    <li className="rounded border border-slate-200 bg-white p-3 text-xs">
      <div className="flex items-start gap-3">
        <span
          className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${severityDotClass(severity)}`}
          title={severity}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-mono text-sm font-semibold">{e.eventName}</span>
            <span className="text-slate-500">{new Date(e.at).toLocaleString()}</span>
            {isTransition && (
              <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-700">
                {e.fromState ?? '—'} → {e.toState}
              </span>
            )}
            <span className="ml-auto rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-600">
              {e.triggeredBy}
            </span>
          </div>
          {summary.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-slate-700">
              {summary.map((s) => (
                <span key={s.label} className="whitespace-nowrap">
                  <span className="text-slate-500">{s.label}: </span>
                  <span className={s.emphasise ? 'font-mono text-red-700' : 'font-mono'}>{s.value}</span>
                </span>
              ))}
            </div>
          )}
          {Object.keys(meta).length > 0 && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="mt-1.5 text-[11px] text-brand-700 hover:underline"
            >
              {open ? 'hide raw meta' : 'show raw meta'}
            </button>
          )}
          {open && (
            <pre className="mt-2 overflow-x-auto rounded bg-slate-50 p-2 text-[11px] leading-snug text-slate-700">
              {JSON.stringify(meta, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </li>
  )
}

type Severity = 'success' | 'failure' | 'warn' | 'info'

function severityFor(eventName: string): Severity {
  if (/ACCEPTED|COMPLETED|OK|CREATED|STORED|DELIVERED/.test(eventName)) return 'success'
  if (/FAILED|REJECTED|CANCELLED/.test(eventName))                       return 'failure'
  if (/RETRY|SIGN|ADMIN\.RESET|ADMIN\.REPROCESS/i.test(eventName))       return 'warn'
  return 'info'
}

function severityDotClass(s: Severity): string {
  switch (s) {
    case 'success': return 'bg-emerald-500'
    case 'failure': return 'bg-red-500'
    case 'warn':    return 'bg-amber-500'
    case 'info':    return 'bg-slate-400'
  }
}

/**
 * Pull the interesting fields out of the meta blob so the operator
 * doesn't have to click into raw JSON for the common case. Keys are
 * ordered by relevance (attempts + errors first, ids + source last).
 */
function summariseMeta(meta: Record<string, unknown>): Array<{ label: string; value: string; emphasise?: boolean }> {
  const out: Array<{ label: string; value: string; emphasise?: boolean }> = []
  const push = (label: string, key: string, emphasise = false) => {
    const v = meta[key]
    if (v === undefined || v === null || v === '') return
    out.push({ label, value: typeof v === 'string' ? v : JSON.stringify(v), emphasise })
  }
  push('Attempts', 'attempts')
  push('Poll attempts', 'pollAttempts')
  push('Reason', 'reason')
  push('NAV error', 'navErrorCode', true)
  push('NAV message', 'navErrorMessage', true)
  push('Last error', 'lastError', true)
  push('NAV tx', 'navTransactionId')
  push('PDF key', 'pdfS3Key')
  push('nav_submission jobs requeued', 'navSubmissionJobsRequeued')
  push('pdf jobs requeued', 'pdfJobsRequeued')
  push('nav jobs reset', 'navJobsReset')
  push('pdf jobs reset', 'pdfJobsReset')
  push('tax_reporting_status reset', 'taxReportingStatusReset')
  push('Source', 'source')
  return out
}

// ── Helpers ───────────────────────────────────────────────────────────

function relativeTime(isoString: string): string {
  const diffMs = new Date(isoString).getTime() - Date.now()
  const absSec = Math.round(Math.abs(diffMs) / 1000)
  const past = diffMs < 0
  if (absSec < 60) return past ? `${absSec}s ago` : `in ${absSec}s`
  const absMin = Math.round(absSec / 60)
  if (absMin < 60) return past ? `${absMin}m ago` : `in ${absMin}m`
  const absH = Math.round(absMin / 60)
  if (absH < 24) return past ? `${absH}h ago` : `in ${absH}h`
  const absD = Math.round(absH / 24)
  return past ? `${absD}d ago` : `in ${absD}d`
}

// ── Fields ────────────────────────────────────────────────────────────

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded border border-slate-200 bg-white p-2">
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className={mono ? 'font-mono text-sm' : 'text-sm'}>{value}</div>
    </div>
  )
}

/**
 * RES-118: render the raw inbound DTO (invoices.invoice_data JSONB) so
 * ops can see exactly what the integrator POSTed. Collapsed by default
 * — the payload can be 200+ lines for a multi-item invoice and would
 * dominate the page. "Copy JSON" writes the pretty-printed form to the
 * clipboard so ops can paste it into a diff / bug ticket without hand-
 * escaping newlines.
 */
function PayloadBlock({ payload }: { payload: unknown }) {
  const [open, setOpen] = useState(false)
  if (payload == null) {
    return <div className="text-xs text-slate-500">No stored payload.</div>
  }
  const pretty = JSON.stringify(payload, null, 2)
  return (
    <div className="rounded border border-slate-200 bg-white p-3 text-xs">
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-brand-700 hover:underline"
        >
          {open ? 'hide payload' : 'show payload'}
        </button>
        <button
          type="button"
          onClick={() => void navigator.clipboard.writeText(pretty)}
          className="text-brand-700 hover:underline"
        >
          copy JSON
        </button>
        <span className="ml-auto text-slate-500">
          {pretty.length.toLocaleString()} chars
        </span>
      </div>
      {open && (
        <pre className="overflow-x-auto rounded bg-slate-50 p-2 text-[11px] leading-snug text-slate-800">
          {pretty}
        </pre>
      )}
    </div>
  )
}

function StorageRow({
  label,
  internalName,
  downloadUrl,
}: {
  label:        ReactNode
  internalName: string | null
  downloadUrl:  string | null
}) {
  if (!internalName) {
    return (
      <div>
        <span className="text-slate-500">{label}: </span>
        <span className="text-slate-400">—</span>
      </div>
    )
  }
  return (
    <div className="flex flex-wrap items-baseline gap-2">
      <span className="text-slate-500">{label}:</span>
      <code className="break-all font-mono text-[11px] text-slate-700">{internalName}</code>
      {downloadUrl ? (
        <a
          href={downloadUrl}
          target="_blank"
          rel="noreferrer"
          className="text-brand-700 hover:underline"
        >
          download →
        </a>
      ) : (
        <span className="text-slate-400 italic">no download link (secret unset?)</span>
      )}
    </div>
  )
}
